import { PERMISSIONS } from '@helios/authz';

/**
 * Navigation contract for the Estimations & Bids hub (approved 2026-10-30).
 * Six primary areas; nine project tabs. Legacy deep tools (takeoff, comping,
 * labour, bid assembly, cost database, ...) keep their URLs and are mapped to
 * the area they belong to so the shell stays consistent around them.
 */
export type EstimatingAreaId =
  | 'command'
  | 'intake'
  | 'projects'
  | 'register'
  | 'intelligence'
  | 'settings';

export interface EstimatingArea {
  id: EstimatingAreaId;
  label: string;
  to: string;
  /** Registry destination id; the shell hides the area when it is not accessible. */
  destinationId: string;
}

export const ESTIMATING_AREAS: readonly EstimatingArea[] = [
  { id: 'command', label: 'Command Center', to: '/estimating/command', destinationId: 'estimating-command' },
  { id: 'intake', label: 'Intake', to: '/estimating/bid-intake', destinationId: 'bid-intake' },
  { id: 'projects', label: 'Projects', to: '/estimating/projects', destinationId: 'estimating-projects' },
  { id: 'register', label: 'Bid Register', to: '/estimating/register', destinationId: 'bid-register' },
  { id: 'intelligence', label: 'Intelligence', to: '/estimating/intelligence', destinationId: 'estimating-intelligence' },
  { id: 'settings', label: 'Settings', to: '/estimating/config', destinationId: 'estimating-config' },
];

export type ProjectTabId =
  | 'overview'
  | 'package'
  | 'scope'
  | 'materials'
  | 'trade-quotes'
  | 'resources'
  | 'schedule'
  | 'cost'
  | 'bid';

export interface ProjectTab {
  id: ProjectTabId;
  label: string;
  /** Section ids (query `?section=`) this tab owns, first is the default. */
  sections?: readonly { id: string; label: string }[];
}

export const PROJECT_TABS: readonly ProjectTab[] = [
  { id: 'overview', label: 'Overview' },
  {
    id: 'package',
    label: 'Package',
    sections: [
      { id: 'drawings', label: 'Drawings' },
      { id: 'specifications', label: 'Specifications' },
      { id: 'addenda', label: 'Addenda' },
      { id: 'files', label: 'Source files' },
    ],
  },
  { id: 'scope', label: 'Scope' },
  {
    id: 'materials',
    label: 'Materials',
    sections: [
      { id: 'pricing', label: 'Pricing comparison' },
      { id: 'lead-times', label: 'Lead times' },
      { id: 'purchase-plan', label: 'Purchase plan' },
    ],
  },
  {
    id: 'trade-quotes',
    label: 'Trade Quotes',
    sections: [
      { id: 'coverage', label: 'Trade coverage' },
      { id: 'comparison', label: 'Quote comparison' },
      { id: 'correspondence', label: 'Correspondence' },
    ],
  },
  {
    id: 'resources',
    label: 'Resources',
    sections: [
      { id: 'labor', label: 'Labor' },
      { id: 'equipment', label: 'Equipment' },
      { id: 'capacity', label: 'Capacity' },
    ],
  },
  { id: 'schedule', label: 'Schedule' },
  { id: 'cost', label: 'Cost' },
  {
    id: 'bid',
    label: 'Bid',
    sections: [
      { id: 'scenarios', label: 'Scenarios' },
      { id: 'proposal', label: 'Proposal' },
      { id: 'approval', label: 'Approval' },
      { id: 'handoff', label: 'Award handoff' },
    ],
  },
];

export function projectHref(estimateId: string, tab: ProjectTabId = 'overview', section?: string): string {
  const base = `/estimating/projects/${encodeURIComponent(estimateId)}/${tab}`;
  return section ? `${base}?section=${encodeURIComponent(section)}` : base;
}

export const INTELLIGENCE_SECTIONS = [
  { id: 'clients', label: 'Clients' },
  { id: 'subcontractors', label: 'Subcontractors' },
  { id: 'comparables', label: 'Comparable projects' },
  { id: 'outcomes', label: 'Outcomes' },
] as const;

export const SETTINGS_SECTIONS = [
  { id: 'pursuit', label: 'Pursuit criteria' },
  { id: 'pricing', label: 'Pricing & margins' },
  { id: 'automation', label: 'Automation' },
  { id: 'sources', label: 'Sources & connections' },
  { id: 'permissions', label: 'Review permissions' },
] as const;

/** Prefix → area for every route the hub owns, including legacy deep tools. */
const AREA_PREFIXES: readonly (readonly [string, EstimatingAreaId])[] = [
  ['/estimating/command', 'command'],
  ['/estimating/bid-intake', 'intake'],
  ['/estimating/leads', 'intake'],
  ['/estimating/register', 'register'],
  ['/estimating/history', 'register'],
  ['/estimating/intelligence', 'intelligence'],
  ['/bid-counterparties', 'intelligence'],
  ['/estimating/competitors', 'intelligence'],
  ['/estimating/config', 'settings'],
];

export function estimatingAreaForPath(pathname: string): EstimatingAreaId {
  const match = AREA_PREFIXES.find(
    ([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
  return match ? match[1] : 'projects';
}

/** Permission gates for the new routes. Specialised grants stay authoritative. */
export const ESTIMATING_ROUTE_PERMISSIONS = {
  command: PERMISSIONS.ESTIMATING_ESTIMATES_READ,
  projects: PERMISSIONS.ESTIMATING_ESTIMATES_READ,
  register: PERMISSIONS.ESTIMATING_ESTIMATES_READ,
  intelligence: PERMISSIONS.ESTIMATING_ESTIMATES_READ,
} as const;
